// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========

import type { RemoteJsonValue } from '@web/api/remoteControl';
import type { CanonicalProjectEvent } from '@web/lib/projector/types';

export type JsonValue = RemoteJsonValue;

export type WorkItemStatus =
  | 'queued'
  | 'in_progress'
  | 'waiting'
  | 'completed'
  | 'failed'
  | 'interrupted';

export type WorkItemLifecycle =
  | 'started'
  | 'delta'
  | 'completed'
  | 'failed'
  | 'interrupted';

export type WorkItemType =
  | 'reasoning_summary'
  | 'tool_call'
  | 'agent_message'
  | 'agent_call'
  | 'human_input'
  | 'plan'
  | 'notice'
  | 'unknown';

export type AgentExecutionRole =
  | 'primary'
  | 'coordinator'
  | 'worker'
  | 'subagent';

export type TextPart = {
  partIndex: number | null;
  sequence: number;
  eventId: string;
  text: string;
};

export type WorkItemError = {
  code?: string;
  message: string;
};

export type BaseWorkItem = {
  /** Attempt-scoped stable key used for storage and React disclosure state. */
  key: string;
  /** Producer-owned item id, stable across lifecycle events. */
  id: string;
  type: WorkItemType;
  schemaVersion: number;
  projectId: string;
  runId: string;
  runVersion: number;
  agentExecutionId: string;
  agentExecutionKey: string;
  status: WorkItemStatus;
  lifecycle: WorkItemLifecycle;
  firstSequence: number;
  lastSequence: number;
  startedAt: string | null;
  completedAt: string | null;
  durationMs: number | null;
};

export type ReasoningSummaryItem = BaseWorkItem & {
  type: 'reasoning_summary';
  summaryText: string;
  parts: TextPart[];
  textTruncated: boolean;
  textSource?: 'parts' | 'explicit';
};

export type ToolCallItem = BaseWorkItem & {
  type: 'tool_call';
  toolCallId: string;
  toolName: string;
  input: JsonValue | null;
  output: string;
  outputParts: TextPart[];
  outputTruncated: boolean;
  outputByteCount: number;
  outputSource?: 'parts' | 'explicit';
  result: JsonValue | null;
  error: WorkItemError | null;
  exitCode: number | null;
  cwd: string | null;
};

export type AgentMessageItem = BaseWorkItem & {
  type: 'agent_message';
  phase: 'commentary' | 'final_answer';
  text: string;
  parts: TextPart[];
  textTruncated: boolean;
  textSource?: 'parts' | 'explicit';
};

export type AgentCallItem = BaseWorkItem & {
  type: 'agent_call';
  childAgentExecutionId: string;
  childAgentExecutionKey: string;
  prompt: string;
  resultSummary: string;
  exchangeId: string | null;
};

export type HumanInputItem = BaseWorkItem & {
  type: 'human_input';
  interactionId: string;
  question: string;
  response: JsonValue | undefined;
};

export type PlanItem = BaseWorkItem & {
  type: 'plan';
  text: string;
  parts: TextPart[];
  textTruncated: boolean;
  textSource?: 'parts' | 'explicit';
};

export type NoticeItem = BaseWorkItem & {
  type: 'notice';
  text: string;
  parts: TextPart[];
  textTruncated: boolean;
  textSource?: 'parts' | 'explicit';
};

export type UnknownWorkItem = BaseWorkItem & {
  type: 'unknown';
  originalType: string;
};

export type WorkItem =
  | ReasoningSummaryItem
  | ToolCallItem
  | AgentMessageItem
  | AgentCallItem
  | HumanInputItem
  | PlanItem
  | NoticeItem
  | UnknownWorkItem;

export type AgentExecutionView = {
  key: string;
  id: string;
  projectId: string;
  runId: string;
  runVersion: number;
  parentId: string | null;
  parentKey: string | null;
  role: AgentExecutionRole;
  name: string;
  status: WorkItemStatus;
  parallelGroupId: string | null;
  itemIds: string[];
  childKeys: string[];
  currentItemId: string | null;
  currentItemKey: string | null;
  currentSummary: string | null;
  startedAt: string | null;
  completedAt: string | null;
  durationMs: number | null;
  toolCount: number;
  isPlaceholder: boolean;
  firstSequence: number;
  lastSequence: number;
};

export type ChatTurnView = {
  key: string;
  id: string;
  projectId: string;
  runId: string;
  runVersion: number;
  status: WorkItemStatus;
  startedAt: string | null;
  completedAt: string | null;
  durationMs: number | null;
  userMessageItemId: string | null;
  rootExecutionKeys: string[];
  finalAnswerItemKey: string | null;
  currentSummary: string | null;
  currentAgentName: string | null;
  activeAgentCount: number;
  itemCount: number;
  lastSequence: number;
};

export type WorkItemProjectionState = {
  seenEventIds: Record<string, true>;
  workItemsById: Record<string, WorkItem>;
  agentExecutionsById: Record<string, AgentExecutionView>;
  turnsById: Record<string, ChatTurnView>;
  pendingHumanInputsById: Record<string, HumanInputItem>;
  unknownEventIds: string[];
  /** Mutable incremental indexes. Consumers should use the exported selectors. */
  runIndexesById: Record<string, RunProjectionIndex>;
  runIndexKeysByRunId: Record<string, string[]>;
  agentAggregatesById: Record<string, AgentProjectionAggregate>;
};

type StatusCounts = Record<WorkItemStatus, number>;

type AgentProjectionAggregate = {
  itemStatusCounts: StatusCounts;
  childStatusCounts: StatusCounts;
  startedAt: string | null;
  latestCompletedAt: string | null;
  lastSequence: number;
  toolCount: number;
  currentChildKey: string | null;
};

type RunProjectionIndex = {
  key: string;
  projectId: string;
  runId: string;
  runVersion: number;
  itemKeys: string[];
  agentKeys: string[];
  rootAgentKeys: string[];
  pendingHumanInputKeys: Record<string, true>;
  pendingHumanInputCount: number;
  schemaVersionOneItemCount: number;
  itemStatusCounts: StatusCounts;
  agentStatusCounts: StatusCounts;
  latestActiveAgentKey: string | null;
  finalAnswerItemKey: string | null;
  startedAt: string | null;
  latestCompletedAt: string | null;
  lastSequence: number;
};

type KnownItemType = Exclude<WorkItemType, 'unknown'>;

type AdaptedWorkItemEvent = {
  schemaVersion: number;
  runVersion: number;
  itemId: string;
  itemType: WorkItemType;
  originalType: string;
  lifecycle: WorkItemLifecycle;
  status: WorkItemStatus;
  agentExecutionId: string;
  parentAgentExecutionId: string | null;
  agentRole: AgentExecutionRole;
  agentName: string;
  toolCallId: string | null;
  exchangeId: string | null;
  humanInteractionId: string | null;
  parallelGroupId: string | null;
  startedAt: string | null;
  completedAt: string | null;
  durationMs: number | null;
  partIndex: number | null;
  textDelta: string;
  summaryText: string;
  summaryTextProvided: boolean;
  text: string;
  textProvided: boolean;
  toolName: string;
  input: JsonValue | undefined;
  outputDelta: string;
  output: string;
  outputProvided: boolean;
  result: JsonValue | undefined;
  error: WorkItemError | null | undefined;
  exitCode: number | null | undefined;
  cwd: string | null | undefined;
  phase: 'commentary' | 'final_answer';
  childAgentExecutionId: string;
  prompt: string;
  resultSummary: string;
  question: string;
  response: JsonValue | undefined;
};

const KNOWN_ITEM_TYPES = new Set<KnownItemType>([
  'reasoning_summary',
  'tool_call',
  'agent_message',
  'agent_call',
  'human_input',
  'plan',
  'notice',
]);

const TERMINAL_STATUSES = new Set<WorkItemStatus>([
  'completed',
  'failed',
  'interrupted',
]);
const ACTIVE_STATUSES = new Set<WorkItemStatus>([
  'queued',
  'in_progress',
  'waiting',
]);
const MAX_STREAM_TEXT_CHARACTERS = 200_000;

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function firstDefined(
  records: Record<string, unknown>[],
  keys: string[]
): unknown {
  for (const record of records) {
    for (const key of keys) {
      if (record[key] !== undefined) return record[key];
    }
  }
  return undefined;
}

function stringValue(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function nullableString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

function numberValue(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function jsonValue(value: unknown): JsonValue | undefined {
  if (
    value === undefined ||
    typeof value === 'function' ||
    typeof value === 'symbol' ||
    typeof value === 'bigint'
  ) {
    return undefined;
  }
  try {
    return JSON.parse(JSON.stringify(value)) as JsonValue;
  } catch {
    return undefined;
  }
}

function timestamp(value: unknown): string | null {
  if (typeof value !== 'string' && typeof value !== 'number') return null;
  const parsed =
    typeof value === 'number'
      ? new Date(value < 10_000_000_000 ? value * 1000 : value)
      : new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
}

function earliestTimestamp(left: string | null, right: string | null) {
  if (!left) return right;
  if (!right) return left;
  return Date.parse(left) <= Date.parse(right) ? left : right;
}

function latestTimestamp(left: string | null, right: string | null) {
  if (!left) return right;
  if (!right) return left;
  return Date.parse(left) >= Date.parse(right) ? left : right;
}

function durationBetween(startedAt: string | null, completedAt: string | null) {
  if (!startedAt || !completedAt) return null;
  const duration = Date.parse(completedAt) - Date.parse(startedAt);
  return Number.isFinite(duration) ? Math.max(0, duration) : null;
}

function roleValue(value: unknown): AgentExecutionRole {
  return value === 'coordinator' ||
    value === 'worker' ||
    value === 'subagent' ||
    value === 'primary'
    ? value
    : 'primary';
}

function lifecycleValue(value: unknown, eventType: string): WorkItemLifecycle {
  if (
    value === 'started' ||
    value === 'delta' ||
    value === 'completed' ||
    value === 'failed' ||
    value === 'interrupted'
  ) {
    return value;
  }
  if (/fail|error/.test(eventType)) return 'failed';
  if (/interrupt|cancel/.test(eventType)) return 'interrupted';
  if (/complete|finish|end/.test(eventType)) return 'completed';
  if (/delta|stream|progress/.test(eventType)) return 'delta';
  return 'started';
}

function defaultStatus(lifecycle: WorkItemLifecycle): WorkItemStatus {
  if (lifecycle === 'failed') return 'failed';
  if (lifecycle === 'interrupted') return 'interrupted';
  if (lifecycle === 'completed') return 'completed';
  return 'in_progress';
}

function statusValue(value: unknown, lifecycle: WorkItemLifecycle) {
  return value === 'queued' ||
    value === 'in_progress' ||
    value === 'waiting' ||
    value === 'completed' ||
    value === 'failed' ||
    value === 'interrupted'
    ? value
    : defaultStatus(lifecycle);
}

function encodeKeyPart(value: string): string {
  return encodeURIComponent(value);
}

export function workItemKey(
  projectId: string,
  runId: string,
  runVersion: number,
  itemType: string,
  itemId: string
): string {
  return [projectId, runId, String(runVersion), itemType, itemId]
    .map(encodeKeyPart)
    .join(':');
}

export function agentExecutionKey(
  projectId: string,
  runId: string,
  runVersion: number,
  executionId: string
): string {
  return [projectId, runId, String(runVersion), 'agent', executionId]
    .map(encodeKeyPart)
    .join(':');
}

export function chatTurnKey(
  projectId: string,
  runId: string,
  runVersion: number
): string {
  return [projectId, runId, String(runVersion), 'turn']
    .map(encodeKeyPart)
    .join(':');
}

function readError(value: unknown): WorkItemError | null | undefined {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (typeof value === 'string') return { message: value };
  const record = asRecord(value);
  const message = stringValue(record.message || record.error);
  if (!message) return null;
  const code = nullableString(record.code);
  return code ? { code, message } : { message };
}

function utf8Length(value: string): number {
  if (typeof TextEncoder !== 'undefined') {
    return new TextEncoder().encode(value).byteLength;
  }
  return value.length;
}

function boundedText(value: string): { text: string; truncated: boolean } {
  if (value.length <= MAX_STREAM_TEXT_CHARACTERS) {
    return { text: value, truncated: false };
  }
  const head = Math.floor(MAX_STREAM_TEXT_CHARACTERS * 0.6);
  const tail = MAX_STREAM_TEXT_CHARACTERS - head;
  return {
    text: `${value.slice(0, head)}\n… output truncated …\n${value.slice(-tail)}`,
    truncated: true,
  };
}

function appendBoundedText(
  current: string,
  delta: string,
  truncated: boolean
): { text: string; truncated: boolean } {
  if (!truncated) return boundedText(current + delta);
  const headLength = Math.floor(MAX_STREAM_TEXT_CHARACTERS * 0.6);
  const tailLength = MAX_STREAM_TEXT_CHARACTERS - headLength;
  const head = current.slice(0, headLength);
  const tail = `${current.slice(-tailLength)}${delta}`.slice(-tailLength);
  return {
    text: `${head}\n… output truncated …\n${tail}`,
    truncated: true,
  };
}

type TextPartMerge = {
  parts: TextPart[];
  change: 'unchanged' | 'appended' | 'reordered';
};

function compareTextParts(left: TextPart, right: TextPart): number {
  if (left.partIndex !== null && right.partIndex !== null) {
    return left.partIndex - right.partIndex || left.sequence - right.sequence;
  }
  return (
    left.sequence - right.sequence || left.eventId.localeCompare(right.eventId)
  );
}

function sameTextPart(left: TextPart, right: TextPart): boolean {
  return left.partIndex === null
    ? right.partIndex === null && left.eventId === right.eventId
    : right.partIndex === left.partIndex;
}

function mergeTextPart(parts: TextPart[], candidate: TextPart): TextPartMerge {
  const last = parts[parts.length - 1];
  if (
    !last ||
    (!sameTextPart(last, candidate) && compareTextParts(last, candidate) < 0)
  ) {
    // Canonical live events are normally monotonic. Mutating this projector-
    // owned array makes the common streaming path O(1); out-of-order replay
    // and replacement parts retain the deterministic fallback below.
    parts.push(candidate);
    return { parts, change: 'appended' };
  }
  const identity =
    candidate.partIndex === null
      ? `event:${candidate.eventId}`
      : `part:${candidate.partIndex}`;
  const existing = parts.find((part) => sameTextPart(part, candidate));
  if (
    existing &&
    (existing.sequence > candidate.sequence ||
      (existing.sequence === candidate.sequence &&
        existing.eventId.localeCompare(candidate.eventId) >= 0))
  ) {
    return { parts, change: 'unchanged' };
  }
  const next = parts.filter((part) =>
    part.partIndex === null
      ? `event:${part.eventId}` !== identity
      : `part:${part.partIndex}` !== identity
  );
  next.push(candidate);
  next.sort(compareTextParts);
  return { parts: next, change: 'reordered' };
}

function partsText(parts: TextPart[]): { text: string; truncated: boolean } {
  return boundedText(parts.map((part) => part.text).join(''));
}

export function createWorkItemProjectionState(): WorkItemProjectionState {
  return {
    seenEventIds: {},
    workItemsById: {},
    agentExecutionsById: {},
    turnsById: {},
    pendingHumanInputsById: {},
    unknownEventIds: [],
    runIndexesById: {},
    runIndexKeysByRunId: {},
    agentAggregatesById: {},
  };
}

function emptyStatusCounts(): StatusCounts {
  return {
    queued: 0,
    in_progress: 0,
    waiting: 0,
    completed: 0,
    failed: 0,
    interrupted: 0,
  };
}

function adjustStatusCounts(
  counts: StatusCounts,
  previous: WorkItemStatus | null,
  next: WorkItemStatus | null
) {
  if (previous === next) return;
  if (previous) counts[previous] = Math.max(0, counts[previous] - 1);
  if (next) counts[next] += 1;
}

function statusCount(counts: StatusCounts): number {
  return Object.values(counts).reduce((total, count) => total + count, 0);
}

function derivedStatus(
  itemCounts: StatusCounts,
  childCounts: StatusCounts,
  fallback: WorkItemStatus
): WorkItemStatus {
  const count = (status: WorkItemStatus) =>
    itemCounts[status] + childCounts[status];
  if (count('queued') + count('in_progress') > 0) return 'in_progress';
  if (count('waiting') > 0) return 'waiting';
  if (statusCount(itemCounts) + statusCount(childCounts) === 0) {
    return fallback;
  }
  if (count('failed') > 0) return 'failed';
  if (count('interrupted') > 0) return 'interrupted';
  return 'completed';
}

function ensureRunIndex(
  state: WorkItemProjectionState,
  projectId: string,
  runId: string,
  runVersion: number
): RunProjectionIndex {
  const key = chatTurnKey(projectId, runId, runVersion);
  const previous = state.runIndexesById[key];
  if (previous) return previous;
  const created: RunProjectionIndex = {
    key,
    projectId,
    runId,
    runVersion,
    itemKeys: [],
    agentKeys: [],
    rootAgentKeys: [],
    pendingHumanInputKeys: {},
    pendingHumanInputCount: 0,
    schemaVersionOneItemCount: 0,
    itemStatusCounts: emptyStatusCounts(),
    agentStatusCounts: emptyStatusCounts(),
    latestActiveAgentKey: null,
    finalAnswerItemKey: null,
    startedAt: null,
    latestCompletedAt: null,
    lastSequence: 0,
  };
  state.runIndexesById[key] = created;
  const keys = state.runIndexKeysByRunId[runId] || [];
  keys.push(key);
  keys.sort(
    (left, right) =>
      state.runIndexesById[left].runVersion -
      state.runIndexesById[right].runVersion
  );
  state.runIndexKeysByRunId[runId] = keys;
  return created;
}

function ensureAgentAggregate(
  state: WorkItemProjectionState,
  agentKey: string
): AgentProjectionAggregate {
  const previous = state.agentAggregatesById[agentKey];
  if (previous) return previous;
  const created: AgentProjectionAggregate = {
    itemStatusCounts: emptyStatusCounts(),
    childStatusCounts: emptyStatusCounts(),
    startedAt: null,
    latestCompletedAt: null,
    lastSequence: 0,
    toolCount: 0,
    currentChildKey: null,
  };
  state.agentAggregatesById[agentKey] = created;
  return created;
}

function appendOrderedKey(
  keys: string[],
  key: string,
  sequenceOf: (candidate: string) => number
) {
  if (keys[keys.length - 1] === key) return;
  const sequence = sequenceOf(key);
  const lastKey = keys[keys.length - 1];
  if (!lastKey || sequenceOf(lastKey) <= sequence) {
    keys.push(key);
    return;
  }
  let low = 0;
  let high = keys.length;
  while (low < high) {
    const middle = (low + high) >>> 1;
    if (sequenceOf(keys[middle]) <= sequence) low = middle + 1;
    else high = middle;
  }
  keys.splice(low, 0, key);
}

function adaptVersionedEvent(
  event: CanonicalProjectEvent
): AdaptedWorkItemEvent | null {
  const payload = asRecord(event.payload);
  const context = asRecord(payload.context);
  const data = asRecord(payload.data);
  const records = [data, payload, context];
  const itemId = nullableString(firstDefined(records, ['item_id', 'itemId']));
  const rawItemType = nullableString(
    firstDefined(records, ['item_type', 'itemType'])
  );
  if (!itemId || !rawItemType) return null;

  const schemaVersion =
    numberValue(firstDefined(records, ['schema_version', 'schemaVersion'])) ??
    0;
  const runVersion =
    schemaVersion === 1 ? Math.max(1, attemptVersion(event)) : 0;
  const known = KNOWN_ITEM_TYPES.has(rawItemType as KnownItemType);
  const itemType: WorkItemType =
    known && (schemaVersion === 0 || schemaVersion === 1)
      ? (rawItemType as KnownItemType)
      : 'unknown';
  const lifecycle = lifecycleValue(
    firstDefined(records, ['lifecycle']),
    event.eventType
  );
  let status = statusValue(firstDefined(records, ['status']), lifecycle);
  if (itemType === 'human_input' && lifecycle === 'started') {
    status = status === 'in_progress' ? 'waiting' : status;
  }
  const agentExecutionId =
    nullableString(
      firstDefined(records, ['agent_execution_id', 'agentExecutionId'])
    ) || `primary:${event.runId}`;
  const childAgentExecutionId =
    nullableString(
      firstDefined(records, [
        'child_agent_execution_id',
        'childAgentExecutionId',
      ])
    ) || '';
  const rawPhase = firstDefined(records, ['phase']);
  const input = firstDefined(records, ['input', 'arguments', 'tool_input']);
  const result = firstDefined(records, ['result', 'tool_result']);
  const response = firstDefined(records, ['response', 'reply']);
  const durationMs = numberValue(
    firstDefined(records, ['duration_ms', 'durationMs'])
  );
  const rawSummaryText = firstDefined(records, ['summary_text', 'summaryText']);
  const rawText = firstDefined(records, ['text', 'content', 'message']);
  const rawOutput = firstDefined(records, ['output']);
  const rawExitCode = firstDefined(records, ['exit_code', 'exitCode']);
  const rawCwd = firstDefined(records, ['cwd', 'working_directory']);

  return {
    schemaVersion,
    runVersion,
    itemId,
    itemType,
    originalType: rawItemType,
    lifecycle,
    status,
    agentExecutionId,
    parentAgentExecutionId: nullableString(
      firstDefined(records, [
        'parent_agent_execution_id',
        'parentAgentExecutionId',
      ])
    ),
    agentRole: roleValue(
      firstDefined(records, ['agent_role', 'agentRole', 'role'])
    ),
    agentName:
      nullableString(
        firstDefined(records, ['agent_name', 'agentName', 'name'])
      ) || 'Agent',
    toolCallId: nullableString(
      firstDefined(records, ['tool_call_id', 'toolCallId'])
    ),
    exchangeId: nullableString(
      firstDefined(records, ['exchange_id', 'exchangeId'])
    ),
    humanInteractionId: nullableString(
      firstDefined(records, ['human_interaction_id', 'humanInteractionId'])
    ),
    parallelGroupId: nullableString(
      firstDefined(records, ['parallel_group_id', 'parallelGroupId'])
    ),
    startedAt: timestamp(firstDefined(records, ['started_at', 'startedAt'])),
    completedAt: timestamp(
      firstDefined(records, ['completed_at', 'completedAt'])
    ),
    durationMs,
    partIndex: numberValue(
      firstDefined(records, [
        'part_index',
        'partIndex',
        'summary_part_index',
        'summaryPartIndex',
      ])
    ),
    textDelta: stringValue(firstDefined(records, ['text_delta', 'textDelta'])),
    summaryText: stringValue(rawSummaryText),
    summaryTextProvided: rawSummaryText !== undefined,
    text: stringValue(rawText),
    textProvided: rawText !== undefined,
    toolName:
      nullableString(
        firstDefined(records, ['tool_name', 'toolName', 'name'])
      ) || 'Tool call',
    input: jsonValue(input),
    outputDelta: stringValue(
      firstDefined(records, ['output_delta', 'outputDelta'])
    ),
    output: stringValue(rawOutput),
    outputProvided: rawOutput !== undefined,
    result: jsonValue(result),
    error: readError(firstDefined(records, ['error'])),
    exitCode: rawExitCode === undefined ? undefined : numberValue(rawExitCode),
    cwd: rawCwd === undefined ? undefined : nullableString(rawCwd),
    phase: rawPhase === 'final_answer' ? 'final_answer' : 'commentary',
    childAgentExecutionId,
    prompt: stringValue(firstDefined(records, ['prompt', 'query', 'task'])),
    resultSummary: stringValue(
      firstDefined(records, ['result_summary', 'resultSummary'])
    ),
    question: stringValue(
      firstDefined(records, ['question', 'prompt', 'content', 'message'])
    ),
    response: jsonValue(response),
  };
}

function adaptLegacyEvent(
  event: CanonicalProjectEvent
): AdaptedWorkItemEvent | null {
  const step = event.legacyStep;
  if (!step || step === 'confirmed' || step === 'human_reply') return null;
  const raw = asRecord(event.payload.__legacy_data ?? event.payload);
  const records = [raw, asRecord(raw.data)];
  const explicitItemId = nullableString(
    firstDefined(records, ['item_id', 'itemId'])
  );
  const itemId = explicitItemId || `legacy:${event.eventId}`;
  const agentName =
    nullableString(
      firstDefined(records, ['agent_name', 'agentName', 'agent', 'name'])
    ) || 'Agent';
  const agentExecutionId =
    nullableString(
      firstDefined(records, ['agent_execution_id', 'agentExecutionId'])
    ) || `primary:${event.runId}`;
  const content = stringValue(
    firstDefined(records, [
      'content',
      'text',
      'message',
      'answer',
      'reply',
      'task',
    ])
  );
  const failed =
    step === 'error' ||
    step === 'failed' ||
    step === 'budget_not_enough' ||
    step === 'context_too_long';
  const finalAnswer = step === 'end' || step === 'wait_confirm';
  const humanInput = step === 'ask';
  const tool =
    step === 'activate_toolkit' ||
    step === 'tool_started' ||
    step === 'tool_finished';
  const itemType: WorkItemType = humanInput
    ? 'human_input'
    : tool
      ? 'tool_call'
      : finalAnswer
        ? 'agent_message'
        : 'notice';
  const lifecycle: WorkItemLifecycle = failed
    ? 'failed'
    : humanInput
      ? 'started'
      : step === 'activate_toolkit' || step === 'tool_started'
        ? 'started'
        : 'completed';
  const status: WorkItemStatus = failed
    ? 'failed'
    : humanInput
      ? 'waiting'
      : lifecycle === 'started'
        ? 'in_progress'
        : 'completed';
  const interactionId =
    nullableString(
      firstDefined(records, ['human_interaction_id', 'humanInteractionId'])
    ) || `legacy:${event.runId}:${itemId}`;
  const toolName =
    nullableString(
      firstDefined(records, [
        'tool_name',
        'toolName',
        'toolkit_name',
        'toolkitName',
        'name',
      ])
    ) || 'Tool call';

  return {
    schemaVersion: 0,
    runVersion: 0,
    itemId,
    itemType,
    originalType: `legacy:${step}`,
    lifecycle,
    status,
    agentExecutionId,
    parentAgentExecutionId: nullableString(
      firstDefined(records, [
        'parent_agent_execution_id',
        'parentAgentExecutionId',
      ])
    ),
    agentRole: roleValue(
      firstDefined(records, ['agent_role', 'agentRole', 'role'])
    ),
    agentName,
    toolCallId:
      nullableString(firstDefined(records, ['tool_call_id', 'toolCallId'])) ||
      (tool ? itemId : null),
    exchangeId: nullableString(
      firstDefined(records, ['exchange_id', 'exchangeId'])
    ),
    humanInteractionId: humanInput ? interactionId : null,
    parallelGroupId: nullableString(
      firstDefined(records, ['parallel_group_id', 'parallelGroupId'])
    ),
    startedAt: lifecycle === 'started' ? event.createdAt : null,
    completedAt: TERMINAL_STATUSES.has(status) ? event.createdAt : null,
    durationMs: numberValue(
      firstDefined(records, ['duration_ms', 'durationMs'])
    ),
    partIndex: null,
    textDelta: '',
    summaryText: '',
    summaryTextProvided: false,
    text: finalAnswer || itemType === 'notice' ? content : '',
    textProvided: finalAnswer || itemType === 'notice',
    toolName,
    input: jsonValue(firstDefined(records, ['input', 'arguments'])),
    outputDelta: '',
    output: stringValue(firstDefined(records, ['output'])),
    outputProvided: firstDefined(records, ['output']) !== undefined,
    result: jsonValue(firstDefined(records, ['result'])),
    error: failed ? readError(firstDefined(records, ['error'])) : undefined,
    exitCode:
      firstDefined(records, ['exit_code', 'exitCode']) === undefined
        ? undefined
        : numberValue(firstDefined(records, ['exit_code', 'exitCode'])),
    cwd:
      firstDefined(records, ['cwd']) === undefined
        ? undefined
        : nullableString(firstDefined(records, ['cwd'])),
    phase: finalAnswer ? 'final_answer' : 'commentary',
    childAgentExecutionId:
      nullableString(
        firstDefined(records, [
          'child_agent_execution_id',
          'childAgentExecutionId',
        ])
      ) || '',
    prompt: '',
    resultSummary: '',
    question: humanInput ? content : '',
    response: undefined,
  };
}

function attemptVersion(event: CanonicalProjectEvent): number {
  if (event.source === 'chat_step_v1') return 0;
  const payload = asRecord(event.payload);
  const context = asRecord(payload.context);
  const explicit = numberValue(
    firstDefined(
      [payload, context],
      [
        'attempt_version',
        'attemptVersion',
        'run_attempt_version',
        'runAttemptVersion',
      ]
    )
  );
  // PR #3's envelope run_version is an aggregate revision and can advance on
  // every event. It must not split one item lifecycle into separate UI rows.
  // Producers that reuse item/execution ids across retries must provide the
  // explicit attempt version above; otherwise a run is one compatibility lane.
  return explicit ?? 1;
}

function baseItem(
  event: CanonicalProjectEvent,
  adapted: AdaptedWorkItemEvent
): BaseWorkItem {
  const runVersion = adapted.runVersion;
  const terminal = TERMINAL_STATUSES.has(adapted.status);
  const startedAt =
    adapted.startedAt ||
    (adapted.lifecycle === 'started' ? event.createdAt : null);
  const completedAt =
    adapted.completedAt || (terminal ? event.createdAt : null);
  return {
    key: workItemKey(
      event.projectId,
      event.runId,
      runVersion,
      adapted.itemType,
      adapted.itemId
    ),
    id: adapted.itemId,
    type: adapted.itemType,
    schemaVersion: adapted.schemaVersion,
    projectId: event.projectId,
    runId: event.runId,
    runVersion,
    agentExecutionId: adapted.agentExecutionId,
    agentExecutionKey: agentExecutionKey(
      event.projectId,
      event.runId,
      runVersion,
      adapted.agentExecutionId
    ),
    status: adapted.status,
    lifecycle: adapted.lifecycle,
    firstSequence: event.runSequence,
    lastSequence: event.runSequence,
    startedAt,
    completedAt,
    durationMs: adapted.durationMs ?? durationBetween(startedAt, completedAt),
  };
}

function deltaPart(
  event: CanonicalProjectEvent,
  adapted: AdaptedWorkItemEvent,
  text: string
): TextPart | null {
  return text
    ? {
        partIndex: adapted.partIndex,
        sequence: event.runSequence,
        eventId: event.eventId,
        text,
      }
    : null;
}

function createWorkItem(
  event: CanonicalProjectEvent,
  adapted: AdaptedWorkItemEvent
): WorkItem {
  const base = baseItem(event, adapted);
  const textPart = deltaPart(event, adapted, adapted.textDelta);
  const parts = textPart ? [textPart] : [];
  switch (adapted.itemType) {
    case 'reasoning_summary': {
      const joined = partsText(parts);
      const explicit = boundedText(adapted.summaryText);
      return {
        ...base,
        type: 'reasoning_summary',
        summaryText: adapted.summaryTextProvided ? explicit.text : joined.text,
        parts,
        textTruncated: adapted.summaryTextProvided
          ? explicit.truncated
          : joined.truncated,
        textSource: adapted.summaryTextProvided ? 'explicit' : 'parts',
      };
    }
    case 'tool_call': {
      const outputPart = deltaPart(event, adapted, adapted.outputDelta);
      const outputParts = outputPart ? [outputPart] : [];
      const joined = partsText(outputParts);
      const explicit = boundedText(adapted.output);
      const output = adapted.outputProvided ? explicit.text : joined.text;
      return {
        ...base,
        type: 'tool_call',
        toolCallId: adapted.toolCallId || adapted.itemId,
        toolName: adapted.toolName,
        input: adapted.input ?? null,
        output,
        outputParts,
        outputTruncated: adapted.outputProvided
          ? explicit.truncated
          : joined.truncated,
        outputByteCount: utf8Length(
          adapted.outputProvided ? adapted.output : joined.text
        ),
        outputSource: adapted.outputProvided ? 'explicit' : 'parts',
        result: adapted.result ?? null,
        error: adapted.error ?? null,
        exitCode: adapted.exitCode ?? null,
        cwd: adapted.cwd ?? null,
      };
    }
    case 'agent_message': {
      const joined = partsText(parts);
      const explicit = boundedText(adapted.text);
      return {
        ...base,
        type: 'agent_message',
        phase: adapted.phase,
        text: adapted.textProvided ? explicit.text : joined.text,
        parts,
        textTruncated: adapted.textProvided
          ? explicit.truncated
          : joined.truncated,
        textSource: adapted.textProvided ? 'explicit' : 'parts',
      };
    }
    case 'agent_call': {
      const childId =
        adapted.childAgentExecutionId || `child:${adapted.itemId}`;
      return {
        ...base,
        type: 'agent_call',
        childAgentExecutionId: childId,
        childAgentExecutionKey: agentExecutionKey(
          event.projectId,
          event.runId,
          adapted.runVersion,
          childId
        ),
        prompt: adapted.prompt,
        resultSummary: adapted.resultSummary,
        exchangeId: adapted.exchangeId,
      };
    }
    case 'human_input':
      return {
        ...base,
        type: 'human_input',
        interactionId:
          adapted.humanInteractionId || `interaction:${adapted.itemId}`,
        question: adapted.question,
        response: adapted.response,
      };
    case 'plan':
    case 'notice': {
      const joined = partsText(parts);
      const explicit = boundedText(adapted.text);
      return {
        ...base,
        type: adapted.itemType,
        text: adapted.textProvided ? explicit.text : joined.text,
        parts,
        textTruncated: adapted.textProvided
          ? explicit.truncated
          : joined.truncated,
        textSource: adapted.textProvided ? 'explicit' : 'parts',
      };
    }
    case 'unknown':
      return {
        ...base,
        type: 'unknown',
        originalType: adapted.originalType,
      };
  }
}

function mergeBase(
  existing: WorkItem,
  event: CanonicalProjectEvent,
  adapted: AdaptedWorkItemEvent
): BaseWorkItem {
  const incomingTerminal = TERMINAL_STATUSES.has(adapted.status);
  const existingTerminal = TERMINAL_STATUSES.has(existing.status);
  const incomingIsNewest = event.runSequence >= existing.lastSequence;
  const acceptLifecycle =
    (incomingTerminal && incomingIsNewest) ||
    (!existingTerminal && incomingIsNewest);
  const status = acceptLifecycle ? adapted.status : existing.status;
  const lifecycle = acceptLifecycle ? adapted.lifecycle : existing.lifecycle;
  const startedAt = earliestTimestamp(
    existing.startedAt,
    adapted.startedAt ||
      (adapted.lifecycle === 'started' ? event.createdAt : null)
  );
  const completedAt = incomingTerminal
    ? latestTimestamp(
        existing.completedAt,
        adapted.completedAt || event.createdAt
      )
    : existing.completedAt;
  return {
    ...existing,
    status,
    lifecycle,
    firstSequence: Math.min(existing.firstSequence, event.runSequence),
    lastSequence: Math.max(existing.lastSequence, event.runSequence),
    startedAt,
    completedAt,
    durationMs:
      adapted.durationMs ??
      existing.durationMs ??
      durationBetween(startedAt, completedAt),
  };
}

function mergeWorkItem(
  existing: WorkItem,
  event: CanonicalProjectEvent,
  adapted: AdaptedWorkItemEvent
): WorkItem {
  const base = mergeBase(existing, event, adapted);
  const incomingIsNewest = event.runSequence >= existing.lastSequence;
  const part = deltaPart(event, adapted, adapted.textDelta);
  switch (existing.type) {
    case 'reasoning_summary': {
      const merged = part
        ? mergeTextPart(existing.parts, part)
        : { parts: existing.parts, change: 'unchanged' as const };
      const explicit = boundedText(adapted.summaryText);
      const useExplicit =
        adapted.summaryTextProvided &&
        (incomingIsNewest || !existing.summaryText);
      const useJoined =
        Boolean(part) &&
        (!existing.summaryText || existing.textSource !== 'explicit');
      const joined = useJoined
        ? merged.change === 'appended' && part
          ? appendBoundedText(
              existing.summaryText,
              part.text,
              existing.textTruncated
            )
          : partsText(merged.parts)
        : { text: existing.summaryText, truncated: existing.textTruncated };
      return {
        ...existing,
        ...base,
        type: 'reasoning_summary',
        summaryText: useExplicit
          ? explicit.text
          : useJoined
            ? joined.text
            : existing.summaryText || joined.text,
        parts: merged.parts,
        textTruncated: useExplicit
          ? explicit.truncated
          : useJoined
            ? joined.truncated
            : existing.textTruncated,
        textSource: useExplicit
          ? 'explicit'
          : useJoined
            ? 'parts'
            : existing.textSource,
      };
    }
    case 'tool_call': {
      const outputPart = deltaPart(event, adapted, adapted.outputDelta);
      const merged = outputPart
        ? mergeTextPart(existing.outputParts, outputPart)
        : { parts: existing.outputParts, change: 'unchanged' as const };
      const explicit = boundedText(adapted.output);
      const useExplicit =
        adapted.outputProvided && (incomingIsNewest || !existing.output);
      const useJoined =
        Boolean(outputPart) &&
        (!existing.output || existing.outputSource !== 'explicit');
      const joined = useJoined
        ? merged.change === 'appended' && outputPart
          ? appendBoundedText(
              existing.output,
              outputPart.text,
              existing.outputTruncated
            )
          : partsText(merged.parts)
        : { text: existing.output, truncated: existing.outputTruncated };
      const output = useExplicit
        ? explicit.text
        : useJoined
          ? joined.text
          : existing.output || joined.text;
      const streamedByteCount = outputPart
        ? merged.change === 'appended'
          ? existing.outputByteCount + utf8Length(outputPart.text)
          : merged.parts.reduce(
              (total, candidate) => total + utf8Length(candidate.text),
              0
            )
        : existing.outputByteCount;
      return {
        ...existing,
        ...base,
        type: 'tool_call',
        toolCallId: adapted.toolCallId || existing.toolCallId,
        toolName:
          adapted.toolName !== 'Tool call' &&
          (incomingIsNewest || existing.toolName === 'Tool call')
            ? adapted.toolName
            : existing.toolName,
        input:
          adapted.input !== undefined &&
          (incomingIsNewest || existing.input === null)
            ? adapted.input
            : existing.input,
        output,
        outputParts: merged.parts,
        outputTruncated:
          useExplicit || useJoined
            ? useExplicit
              ? explicit.truncated
              : joined.truncated
            : existing.outputTruncated,
        outputByteCount: Math.max(
          existing.outputByteCount,
          adapted.outputProvided
            ? utf8Length(adapted.output)
            : streamedByteCount
        ),
        outputSource: useExplicit
          ? 'explicit'
          : useJoined
            ? 'parts'
            : existing.outputSource,
        result:
          adapted.result !== undefined &&
          (incomingIsNewest || existing.result === null)
            ? adapted.result
            : existing.result,
        error:
          adapted.error !== undefined &&
          (incomingIsNewest || existing.error === null)
            ? adapted.error
            : existing.error,
        exitCode:
          adapted.exitCode !== undefined &&
          (incomingIsNewest || existing.exitCode === null)
            ? adapted.exitCode
            : existing.exitCode,
        cwd:
          adapted.cwd !== undefined &&
          (incomingIsNewest || existing.cwd === null)
            ? adapted.cwd
            : existing.cwd,
      };
    }
    case 'agent_message': {
      const merged = part
        ? mergeTextPart(existing.parts, part)
        : { parts: existing.parts, change: 'unchanged' as const };
      const explicit = boundedText(adapted.text);
      const useExplicit =
        adapted.textProvided && (incomingIsNewest || !existing.text);
      const useJoined =
        Boolean(part) && (!existing.text || existing.textSource !== 'explicit');
      const joined = useJoined
        ? merged.change === 'appended' && part
          ? appendBoundedText(existing.text, part.text, existing.textTruncated)
          : partsText(merged.parts)
        : { text: existing.text, truncated: existing.textTruncated };
      return {
        ...existing,
        ...base,
        type: 'agent_message',
        phase:
          adapted.phase === 'final_answer' && incomingIsNewest
            ? 'final_answer'
            : existing.phase,
        text: useExplicit
          ? explicit.text
          : useJoined
            ? joined.text
            : existing.text || joined.text,
        parts: merged.parts,
        textTruncated:
          useExplicit || useJoined
            ? useExplicit
              ? explicit.truncated
              : joined.truncated
            : existing.textTruncated,
        textSource: useExplicit
          ? 'explicit'
          : useJoined
            ? 'parts'
            : existing.textSource,
      };
    }
    case 'agent_call': {
      const childId =
        adapted.childAgentExecutionId || existing.childAgentExecutionId;
      return {
        ...existing,
        ...base,
        type: 'agent_call',
        childAgentExecutionId: childId,
        childAgentExecutionKey: agentExecutionKey(
          existing.projectId,
          existing.runId,
          existing.runVersion,
          childId
        ),
        prompt:
          adapted.prompt && (incomingIsNewest || !existing.prompt)
            ? adapted.prompt
            : existing.prompt,
        resultSummary:
          adapted.resultSummary && (incomingIsNewest || !existing.resultSummary)
            ? adapted.resultSummary
            : existing.resultSummary,
        exchangeId: adapted.exchangeId || existing.exchangeId,
      };
    }
    case 'human_input':
      return {
        ...existing,
        ...base,
        type: 'human_input',
        interactionId: adapted.humanInteractionId || existing.interactionId,
        question:
          adapted.question && (incomingIsNewest || !existing.question)
            ? adapted.question
            : existing.question,
        response:
          adapted.response !== undefined ? adapted.response : existing.response,
      };
    case 'plan':
    case 'notice': {
      const merged = part
        ? mergeTextPart(existing.parts, part)
        : { parts: existing.parts, change: 'unchanged' as const };
      const explicit = boundedText(adapted.text);
      const useExplicit =
        adapted.textProvided && (incomingIsNewest || !existing.text);
      const useJoined =
        Boolean(part) && (!existing.text || existing.textSource !== 'explicit');
      const joined = useJoined
        ? merged.change === 'appended' && part
          ? appendBoundedText(existing.text, part.text, existing.textTruncated)
          : partsText(merged.parts)
        : { text: existing.text, truncated: existing.textTruncated };
      return {
        ...existing,
        ...base,
        type: existing.type,
        text: useExplicit
          ? explicit.text
          : useJoined
            ? joined.text
            : existing.text || joined.text,
        parts: merged.parts,
        textTruncated:
          useExplicit || useJoined
            ? useExplicit
              ? explicit.truncated
              : joined.truncated
            : existing.textTruncated,
        textSource: useExplicit
          ? 'explicit'
          : useJoined
            ? 'parts'
            : existing.textSource,
      };
    }
    case 'unknown':
      return { ...existing, ...base, type: 'unknown' };
  }
}

function emptyAgent(
  event: CanonicalProjectEvent,
  executionId: string,
  options: {
    runVersion?: number;
    parentId?: string | null;
    role?: AgentExecutionRole;
    name?: string;
    parallelGroupId?: string | null;
    placeholder?: boolean;
  } = {}
): AgentExecutionView {
  const runVersion = options.runVersion ?? attemptVersion(event);
  const parentId = options.parentId || null;
  return {
    key: agentExecutionKey(
      event.projectId,
      event.runId,
      runVersion,
      executionId
    ),
    id: executionId,
    projectId: event.projectId,
    runId: event.runId,
    runVersion,
    parentId,
    parentKey: parentId
      ? agentExecutionKey(event.projectId, event.runId, runVersion, parentId)
      : null,
    role: options.role || 'subagent',
    name: options.name || 'Agent',
    status: 'in_progress',
    parallelGroupId: options.parallelGroupId || null,
    itemIds: [],
    childKeys: [],
    currentItemId: null,
    currentItemKey: null,
    currentSummary: null,
    startedAt: null,
    completedAt: null,
    durationMs: null,
    toolCount: 0,
    isPlaceholder: options.placeholder ?? true,
    firstSequence: event.runSequence,
    lastSequence: event.runSequence,
  };
}

function ensureAgent(
  state: WorkItemProjectionState,
  event: CanonicalProjectEvent,
  executionId: string,
  options: {
    runVersion?: number;
    parentId?: string | null;
    role?: AgentExecutionRole;
    name?: string;
    parallelGroupId?: string | null;
    placeholder?: boolean;
  } = {}
): AgentExecutionView {
  const runVersion = options.runVersion ?? attemptVersion(event);
  const key = agentExecutionKey(
    event.projectId,
    event.runId,
    runVersion,
    executionId
  );
  const previous = state.agentExecutionsById[key];
  const runIndex = ensureRunIndex(
    state,
    event.projectId,
    event.runId,
    runVersion
  );
  const parentId = options.parentId ?? previous?.parentId ?? null;
  const actualUpdate = options.placeholder === false;
  const next = previous
    ? {
        ...previous,
        parentId,
        parentKey: parentId
          ? agentExecutionKey(
              event.projectId,
              event.runId,
              runVersion,
              parentId
            )
          : null,
        role: actualUpdate ? options.role || previous.role : previous.role,
        name:
          actualUpdate && options.name && options.name !== 'Agent'
            ? options.name
            : previous.name,
        parallelGroupId:
          actualUpdate && options.parallelGroupId != null
            ? options.parallelGroupId
            : previous.parallelGroupId,
        isPlaceholder: actualUpdate ? false : previous.isPlaceholder,
        firstSequence: Math.min(previous.firstSequence, event.runSequence),
        lastSequence: Math.max(previous.lastSequence, event.runSequence),
      }
    : emptyAgent(event, executionId, { ...options, runVersion });
  state.agentExecutionsById[key] = next;
  ensureAgentAggregate(state, key);
  if (!previous) {
    runIndex.agentKeys.push(key);
  }
  const wasRoot = Boolean(previous && !previous.parentKey);
  const isRoot = !next.parentKey;
  if (isRoot && !wasRoot) {
    appendOrderedKey(
      runIndex.rootAgentKeys,
      key,
      (candidate) => state.agentExecutionsById[candidate].firstSequence
    );
  } else if (!isRoot && wasRoot) {
    const rootIndex = runIndex.rootAgentKeys.indexOf(key);
    if (rootIndex >= 0) runIndex.rootAgentKeys.splice(rootIndex, 1);
  }
  syncRunAgentStatus(state, previous || null, next);

  if (parentId) {
    const parent = ensureAgent(state, event, parentId, {
      runVersion,
      role: 'primary',
      placeholder: true,
    });
    if (!previous || previous.parentKey !== parent.key) {
      appendOrderedKey(
        parent.childKeys,
        key,
        (candidate) => state.agentExecutionsById[candidate].firstSequence
      );
      state.agentExecutionsById[parent.key] = parent;
      const parentAggregate = ensureAgentAggregate(state, parent.key);
      adjustStatusCounts(parentAggregate.childStatusCounts, null, next.status);
      parentAggregate.lastSequence = Math.max(
        parentAggregate.lastSequence,
        next.lastSequence
      );
    }
  }
  return next;
}

function cleanSummary(value: string): string | null {
  // The live summary never displays more than 240 characters. Bounding the
  // normalization input avoids flattening a long streaming string per delta.
  const sample = value.length > 1_024 ? value.slice(0, 1_024) : value;
  const normalized = sample.replace(/\s+/g, ' ').trim();
  if (!normalized) return null;
  return normalized.length > 240 || value.length > sample.length
    ? `${normalized.slice(0, 239)}…`
    : normalized;
}

function itemSummary(item: WorkItem): string | null {
  switch (item.type) {
    case 'reasoning_summary':
      return cleanSummary(
        item.summaryText || item.parts[item.parts.length - 1]?.text || ''
      );
    case 'tool_call':
      return cleanSummary(
        item.status === 'in_progress'
          ? `Using ${item.toolName}`
          : `${item.toolName} ${item.status}`
      );
    case 'agent_message':
      return cleanSummary(item.text);
    case 'agent_call':
      return cleanSummary(item.prompt || 'Delegating work to a subagent');
    case 'human_input':
      return cleanSummary(item.question || 'Waiting for your input');
    case 'plan':
    case 'notice':
      return cleanSummary(item.text);
    case 'unknown':
      return `Processing ${item.originalType}`;
  }
}

const CURRENT_ITEM_PRIORITY: Record<WorkItemType, number> = {
  human_input: 0,
  tool_call: 1,
  reasoning_summary: 2,
  agent_call: 3,
  plan: 4,
  notice: 5,
  agent_message: 6,
  unknown: 7,
};

function currentItemIsBetter(left: WorkItem, right: WorkItem): boolean {
  return (
    CURRENT_ITEM_PRIORITY[left.type] < CURRENT_ITEM_PRIORITY[right.type] ||
    (CURRENT_ITEM_PRIORITY[left.type] === CURRENT_ITEM_PRIORITY[right.type] &&
      (left.lastSequence > right.lastSequence ||
        (left.lastSequence === right.lastSequence &&
          left.key.localeCompare(right.key) < 0)))
  );
}

function activeSummaryItem(
  state: WorkItemProjectionState,
  itemKey: string | null
): WorkItem | null {
  if (!itemKey) return null;
  const item = state.workItemsById[itemKey];
  return item && ACTIVE_STATUSES.has(item.status) && itemSummary(item)
    ? item
    : null;
}

function refreshAgent(
  state: WorkItemProjectionState,
  agent: AgentExecutionView,
  changedItemKey: string | null,
  changedChildKey: string | null
): AgentExecutionView {
  const aggregate = ensureAgentAggregate(state, agent.key);
  let currentItem = activeSummaryItem(state, agent.currentItemKey);
  const changedItem = activeSummaryItem(state, changedItemKey);
  if (
    changedItem &&
    (!currentItem || currentItemIsBetter(changedItem, currentItem))
  ) {
    currentItem = changedItem;
  } else if (!currentItem && agent.currentItemKey && agent.itemIds.length) {
    for (const itemKey of agent.itemIds) {
      const candidate = activeSummaryItem(state, itemKey);
      if (
        candidate &&
        (!currentItem || currentItemIsBetter(candidate, currentItem))
      ) {
        currentItem = candidate;
      }
    }
  }

  const activeSummaryChild = (key: string | null) => {
    if (!key) return null;
    const child = state.agentExecutionsById[key];
    return child && ACTIVE_STATUSES.has(child.status) && child.currentSummary
      ? child
      : null;
  };
  const childIsNewer = (left: AgentExecutionView, right: AgentExecutionView) =>
    left.lastSequence > right.lastSequence ||
    (left.lastSequence === right.lastSequence &&
      left.key.localeCompare(right.key) < 0);
  let currentChild = activeSummaryChild(aggregate.currentChildKey);
  const changedChild = activeSummaryChild(changedChildKey);
  if (
    changedChild &&
    (!currentChild || childIsNewer(changedChild, currentChild))
  ) {
    currentChild = changedChild;
  } else if (!currentChild && aggregate.currentChildKey) {
    for (const childKey of agent.childKeys) {
      const candidate = activeSummaryChild(childKey);
      if (
        candidate &&
        (!currentChild || childIsNewer(candidate, currentChild))
      ) {
        currentChild = candidate;
      }
    }
  }
  aggregate.currentChildKey = currentChild?.key || null;

  const status = derivedStatus(
    aggregate.itemStatusCounts,
    aggregate.childStatusCounts,
    agent.status
  );
  const completedAt = ACTIVE_STATUSES.has(status)
    ? null
    : aggregate.latestCompletedAt;
  return {
    ...agent,
    status,
    currentItemId: currentItem?.id || null,
    currentItemKey: currentItem?.key || null,
    currentSummary:
      (currentItem && itemSummary(currentItem)) ||
      currentChild?.currentSummary ||
      agent.currentSummary,
    startedAt: aggregate.startedAt,
    completedAt,
    durationMs: durationBetween(aggregate.startedAt, completedAt),
    toolCount: aggregate.toolCount,
    lastSequence: Math.max(agent.lastSequence, aggregate.lastSequence),
  };
}

function findLatestActiveAgent(
  state: WorkItemProjectionState,
  index: RunProjectionIndex
): AgentExecutionView | null {
  let latest: AgentExecutionView | null = null;
  for (const key of index.agentKeys) {
    const agent = state.agentExecutionsById[key];
    if (
      agent &&
      !agent.isPlaceholder &&
      ACTIVE_STATUSES.has(agent.status) &&
      (!latest ||
        agent.lastSequence > latest.lastSequence ||
        (agent.lastSequence === latest.lastSequence &&
          agent.key.localeCompare(latest.key) < 0))
    ) {
      latest = agent;
    }
  }
  index.latestActiveAgentKey = latest?.key || null;
  return latest;
}

function syncRunAgentStatus(
  state: WorkItemProjectionState,
  previous: AgentExecutionView | null,
  next: AgentExecutionView
) {
  const index = ensureRunIndex(
    state,
    next.projectId,
    next.runId,
    next.runVersion
  );
  adjustStatusCounts(
    index.agentStatusCounts,
    previous && !previous.isPlaceholder ? previous.status : null,
    !next.isPlaceholder ? next.status : null
  );
  if (!next.isPlaceholder && ACTIVE_STATUSES.has(next.status)) {
    const latest = index.latestActiveAgentKey
      ? state.agentExecutionsById[index.latestActiveAgentKey]
      : null;
    if (
      !latest ||
      next.lastSequence > latest.lastSequence ||
      (next.lastSequence === latest.lastSequence &&
        next.key.localeCompare(latest.key) < 0)
    ) {
      index.latestActiveAgentKey = next.key;
    }
  } else if (index.latestActiveAgentKey === next.key) {
    const remainingActive =
      index.agentStatusCounts.queued +
      index.agentStatusCounts.in_progress +
      index.agentStatusCounts.waiting;
    if (remainingActive === 0) index.latestActiveAgentKey = null;
    else findLatestActiveAgent(state, index);
  }
}

function refreshAgentChain(
  state: WorkItemProjectionState,
  ownerKey: string,
  changedItemKey: string
) {
  let key: string | null = ownerKey;
  let itemKey: string | null = changedItemKey;
  let childKey: string | null = null;
  const seen = new Set<string>();
  while (key && !seen.has(key)) {
    seen.add(key);
    const previous = state.agentExecutionsById[key];
    if (!previous) break;
    const refreshed = refreshAgent(state, previous, itemKey, childKey);
    state.agentExecutionsById[key] = refreshed;
    syncRunAgentStatus(state, previous, refreshed);

    const parentKey: string | null = refreshed.parentKey;
    if (!parentKey) break;
    const parentAggregate = ensureAgentAggregate(state, parentKey);
    adjustStatusCounts(
      parentAggregate.childStatusCounts,
      previous.status,
      refreshed.status
    );
    parentAggregate.startedAt = earliestTimestamp(
      parentAggregate.startedAt,
      refreshed.startedAt
    );
    parentAggregate.latestCompletedAt = latestTimestamp(
      parentAggregate.latestCompletedAt,
      refreshed.completedAt
    );
    parentAggregate.lastSequence = Math.max(
      parentAggregate.lastSequence,
      refreshed.lastSequence
    );
    key = parentKey;
    itemKey = null;
    childKey = refreshed.key;
  }
}

function validRootFinalAnswer(
  state: WorkItemProjectionState,
  itemKey: string | null
): AgentMessageItem | null {
  if (!itemKey) return null;
  const item = state.workItemsById[itemKey];
  if (item?.type !== 'agent_message' || item.phase !== 'final_answer') {
    return null;
  }
  const owner = state.agentExecutionsById[item.agentExecutionKey];
  return owner &&
    !owner.parentKey &&
    (owner.role === 'primary' || owner.role === 'coordinator')
    ? item
    : null;
}

function refreshTurn(
  state: WorkItemProjectionState,
  index: RunProjectionIndex
) {
  let finalAnswer = validRootFinalAnswer(state, index.finalAnswerItemKey);
  if (!finalAnswer && index.finalAnswerItemKey) {
    index.finalAnswerItemKey = null;
    for (const itemKey of index.itemKeys) {
      const candidate = validRootFinalAnswer(state, itemKey);
      if (
        candidate &&
        (!finalAnswer || candidate.lastSequence > finalAnswer.lastSequence)
      ) {
        finalAnswer = candidate;
      }
    }
    index.finalAnswerItemKey = finalAnswer?.key || null;
  }
  const activeAgentCount =
    index.agentStatusCounts.queued +
    index.agentStatusCounts.in_progress +
    index.agentStatusCounts.waiting;
  const latestActiveAgent = activeAgentCount
    ? index.latestActiveAgentKey
      ? state.agentExecutionsById[index.latestActiveAgentKey]
      : findLatestActiveAgent(state, index)
    : null;
  const hasRunningAgent =
    index.agentStatusCounts.queued + index.agentStatusCounts.in_progress > 0;
  let status: WorkItemStatus;
  if (index.pendingHumanInputCount > 0 && !hasRunningAgent) {
    status = 'waiting';
  } else if (activeAgentCount > 0) {
    status = 'in_progress';
  } else if (finalAnswer) {
    status = finalAnswer.status;
  } else if (index.itemKeys.length) {
    status = index.itemStatusCounts.failed
      ? 'failed'
      : index.itemStatusCounts.interrupted
        ? 'interrupted'
        : 'completed';
  } else {
    status = 'queued';
  }
  const completedAt = ACTIVE_STATUSES.has(status)
    ? null
    : index.latestCompletedAt;
  state.turnsById[index.key] = {
    key: index.key,
    id: index.runId,
    projectId: index.projectId,
    runId: index.runId,
    runVersion: index.runVersion,
    status,
    startedAt: index.startedAt,
    completedAt,
    durationMs: durationBetween(index.startedAt, completedAt),
    userMessageItemId: null,
    rootExecutionKeys: index.rootAgentKeys,
    finalAnswerItemKey: finalAnswer?.key || null,
    currentSummary:
      latestActiveAgent?.currentSummary ||
      (finalAnswer ? itemSummary(finalAnswer) : null),
    currentAgentName: latestActiveAgent?.name || null,
    activeAgentCount,
    itemCount: index.itemKeys.length,
    lastSequence: index.lastSequence,
  };
}

function adaptLegacyReply(
  state: WorkItemProjectionState,
  event: CanonicalProjectEvent
): AdaptedWorkItemEvent | null {
  if (event.legacyStep !== 'human_reply') return null;
  const payload = asRecord(event.payload);
  const raw = asRecord(payload.__legacy_data ?? payload);
  const records = [
    raw,
    asRecord(raw.data),
    payload,
    asRecord(payload.context),
    asRecord(payload.data),
  ];
  const interactionId = nullableString(
    firstDefined(records, ['human_interaction_id', 'humanInteractionId'])
  );
  const itemId = nullableString(firstDefined(records, ['item_id', 'itemId']));
  const agentExecutionId = nullableString(
    firstDefined(records, ['agent_execution_id', 'agentExecutionId'])
  );
  const candidates = Object.values(state.pendingHumanInputsById).filter(
    (item) => item.projectId === event.projectId && item.runId === event.runId
  );
  let pending: HumanInputItem | undefined;
  if (interactionId) {
    pending = candidates.find((item) => item.interactionId === interactionId);
  } else if (itemId) {
    pending = candidates.find((item) => item.id === itemId);
  } else if (agentExecutionId) {
    const owned = candidates.filter(
      (item) => item.agentExecutionId === agentExecutionId
    );
    if (owned.length === 1) pending = owned[0];
  } else {
    // A pure legacy reply has no stable interaction identity. It may close the
    // only legacy ask in the run, but must never guess between concurrent asks
    // or consume a versioned interaction from the dual-read lane.
    const legacyCandidates = candidates.filter(
      (item) => item.schemaVersion === 0
    );
    if (legacyCandidates.length === 1) pending = legacyCandidates[0];
  }
  if (!pending) return null;
  const response = jsonValue(
    firstDefined(records, ['reply', 'response', 'content', 'text']) ?? null
  );
  const owner = state.agentExecutionsById[pending.agentExecutionKey];
  return {
    schemaVersion: pending.schemaVersion,
    runVersion: pending.runVersion,
    itemId: pending.id,
    itemType: 'human_input',
    originalType: 'legacy:human_reply',
    lifecycle: 'completed',
    status: 'completed',
    agentExecutionId: pending.agentExecutionId,
    parentAgentExecutionId: owner?.parentId || null,
    agentRole: owner?.role || 'primary',
    agentName: owner?.name || 'Agent',
    toolCallId: null,
    exchangeId: null,
    humanInteractionId: pending.interactionId,
    parallelGroupId: owner?.parallelGroupId || null,
    startedAt: pending.startedAt,
    completedAt: event.createdAt,
    durationMs: null,
    partIndex: null,
    textDelta: '',
    summaryText: '',
    summaryTextProvided: false,
    text: '',
    textProvided: false,
    toolName: 'Tool call',
    input: undefined,
    outputDelta: '',
    output: '',
    outputProvided: false,
    result: undefined,
    error: undefined,
    exitCode: undefined,
    cwd: undefined,
    phase: 'commentary',
    childAgentExecutionId: '',
    prompt: '',
    resultSummary: '',
    question: pending.question,
    response,
  };
}

/** Incrementally materialize one accepted canonical event into typed UI state. */
export function reduceWorkItemProjection(
  state: WorkItemProjectionState,
  event: CanonicalProjectEvent
): WorkItemProjectionState {
  if (state.seenEventIds[event.eventId]) return state;
  // Projection records are an append-only accumulator owned by the projector.
  // Mutating the indexed records avoids copying every prior event/item on the
  // WebSocket path; the returned shallow shell still changes for consumers.
  state.runIndexesById ||= {};
  state.runIndexKeysByRunId ||= {};
  state.agentAggregatesById ||= {};
  state.seenEventIds[event.eventId] = true;
  const next: WorkItemProjectionState = { ...state };
  // Versioned item identity is authoritative even when rolling-deployment
  // events still carry legacy_step=human_reply for old clients.
  const adapted =
    adaptVersionedEvent(event) ||
    adaptLegacyReply(state, event) ||
    adaptLegacyEvent(event);
  if (!adapted) return next;
  if (adapted.itemType === 'unknown') {
    next.unknownEventIds.push(event.eventId);
  }

  const candidate = createWorkItem(event, adapted);
  const existing = next.workItemsById[candidate.key];
  const item = existing ? mergeWorkItem(existing, event, adapted) : candidate;
  next.workItemsById[item.key] = item;
  const runIndex = ensureRunIndex(
    next,
    event.projectId,
    event.runId,
    adapted.runVersion
  );
  if (!existing) {
    appendOrderedKey(
      runIndex.itemKeys,
      item.key,
      (key) => next.workItemsById[key].firstSequence
    );
    if (item.schemaVersion === 1) runIndex.schemaVersionOneItemCount += 1;
  }
  adjustStatusCounts(
    runIndex.itemStatusCounts,
    existing?.status || null,
    item.status
  );
  runIndex.startedAt = earliestTimestamp(runIndex.startedAt, item.startedAt);
  runIndex.latestCompletedAt = latestTimestamp(
    runIndex.latestCompletedAt,
    item.completedAt
  );
  runIndex.lastSequence = Math.max(runIndex.lastSequence, item.lastSequence);

  const owner = ensureAgent(next, event, adapted.agentExecutionId, {
    runVersion: adapted.runVersion,
    parentId: adapted.parentAgentExecutionId,
    role: adapted.agentRole,
    name: adapted.agentName,
    parallelGroupId: adapted.parallelGroupId,
    placeholder: false,
  });
  const ownerAggregate = ensureAgentAggregate(next, owner.key);
  if (
    !existing ||
    (existing.agentExecutionKey !== owner.key &&
      !owner.itemIds.includes(item.key))
  ) {
    appendOrderedKey(
      owner.itemIds,
      item.key,
      (key) => next.workItemsById[key].firstSequence
    );
    if (item.type === 'tool_call') ownerAggregate.toolCount += 1;
  }
  adjustStatusCounts(
    ownerAggregate.itemStatusCounts,
    existing?.agentExecutionKey === owner.key ? existing.status : null,
    item.status
  );
  ownerAggregate.startedAt = earliestTimestamp(
    ownerAggregate.startedAt,
    item.startedAt
  );
  ownerAggregate.latestCompletedAt = latestTimestamp(
    ownerAggregate.latestCompletedAt,
    item.completedAt
  );
  ownerAggregate.lastSequence = Math.max(
    ownerAggregate.lastSequence,
    item.lastSequence
  );
  next.agentExecutionsById[owner.key] = owner;

  const previousPending =
    existing?.type === 'human_input' && existing.status === 'waiting'
      ? existing
      : null;
  const currentPending =
    item.type === 'human_input' && item.status === 'waiting' ? item : null;
  if (
    previousPending &&
    previousPending.interactionId !== currentPending?.interactionId
  ) {
    delete next.pendingHumanInputsById[previousPending.interactionId];
    if (runIndex.pendingHumanInputKeys[previousPending.interactionId]) {
      delete runIndex.pendingHumanInputKeys[previousPending.interactionId];
      runIndex.pendingHumanInputCount = Math.max(
        0,
        runIndex.pendingHumanInputCount - 1
      );
    }
  }
  if (currentPending) {
    next.pendingHumanInputsById[currentPending.interactionId] = currentPending;
    if (!runIndex.pendingHumanInputKeys[currentPending.interactionId]) {
      runIndex.pendingHumanInputKeys[currentPending.interactionId] = true;
      runIndex.pendingHumanInputCount += 1;
    }
  }

  if (item.type === 'agent_call') {
    ensureAgent(next, event, item.childAgentExecutionId, {
      runVersion: adapted.runVersion,
      parentId: owner.id,
      role: 'subagent',
      name: 'Subagent',
      parallelGroupId: adapted.parallelGroupId,
      placeholder: true,
    });
  }

  const rootFinalAnswer = validRootFinalAnswer(next, item.key);
  const previousFinalAnswer = validRootFinalAnswer(
    next,
    runIndex.finalAnswerItemKey
  );
  if (
    rootFinalAnswer &&
    (!previousFinalAnswer ||
      rootFinalAnswer.lastSequence >= previousFinalAnswer.lastSequence)
  ) {
    runIndex.finalAnswerItemKey = rootFinalAnswer.key;
  }
  refreshAgentChain(next, owner.key, item.key);
  refreshTurn(next, runIndex);
  return next;
}

/** Deterministic replay helper used by snapshot hydration and fixtures. */
export function projectWorkItemEvents(
  events: CanonicalProjectEvent[],
  initial: WorkItemProjectionState = createWorkItemProjectionState()
): WorkItemProjectionState {
  const ordered = [...events].sort((left, right) => {
    if (left.projectId !== right.projectId) {
      return left.projectId.localeCompare(right.projectId);
    }
    if (left.runId !== right.runId)
      return left.runId.localeCompare(right.runId);
    if (left.runSequence !== right.runSequence) {
      return left.runSequence - right.runSequence;
    }
    const leftVersion = attemptVersion(left);
    const rightVersion = attemptVersion(right);
    if (leftVersion !== rightVersion) return leftVersion - rightVersion;
    return left.eventId.localeCompare(right.eventId);
  });
  return ordered.reduce(reduceWorkItemProjection, initial);
}

export function selectChatTurn(
  state: WorkItemProjectionState,
  runId: string,
  runVersion?: number
): ChatTurnView | null {
  const matches = (state.runIndexKeysByRunId?.[runId] || [])
    .map((key) => state.turnsById[key])
    .filter(
      (turn): turn is ChatTurnView =>
        Boolean(turn) &&
        (runVersion === undefined || turn.runVersion === runVersion)
    )
    .sort(
      (left, right) =>
        right.runVersion - left.runVersion ||
        right.lastSequence - left.lastSequence
    );
  return matches[0] || null;
}

function runIndexes(
  state: WorkItemProjectionState,
  runId: string,
  runVersion?: number
): RunProjectionIndex[] {
  return (state.runIndexKeysByRunId?.[runId] || [])
    .map((key) => state.runIndexesById[key])
    .filter(
      (index): index is RunProjectionIndex =>
        Boolean(index) &&
        (runVersion === undefined || index.runVersion === runVersion)
    );
}

export function selectWorkItemsForRun(
  state: WorkItemProjectionState,
  runId: string,
  runVersion?: number
): WorkItem[] {
  return runIndexes(state, runId, runVersion).flatMap((index) =>
    index.itemKeys
      .map((key) => state.workItemsById[key])
      .filter((item): item is WorkItem => Boolean(item))
  );
}

export function hasVersionedWorkItemsForRun(
  state: WorkItemProjectionState,
  runId: string
): boolean {
  return runIndexes(state, runId).some(
    (index) => index.schemaVersionOneItemCount > 0
  );
}

export function selectAgentExecutionsForRun(
  state: WorkItemProjectionState,
  runId: string,
  runVersion: number
): AgentExecutionView[] {
  return runIndexes(state, runId, runVersion).flatMap((index) =>
    index.agentKeys
      .map((key) => state.agentExecutionsById[key])
      .filter((agent): agent is AgentExecutionView => Boolean(agent))
  );
}

export function selectAgentExecution(
  state: WorkItemProjectionState,
  executionKeyOrId: string,
  runId?: string
): AgentExecutionView | null {
  const direct = state.agentExecutionsById[executionKeyOrId];
  if (direct) return direct;
  return (
    Object.values(state.agentExecutionsById).find(
      (agent) =>
        agent.id === executionKeyOrId &&
        (runId === undefined || agent.runId === runId)
    ) || null
  );
}

export function selectFinalAnswer(
  state: WorkItemProjectionState,
  runId: string,
  runVersion?: number
): AgentMessageItem | null {
  const turn = selectChatTurn(state, runId, runVersion);
  if (!turn?.finalAnswerItemKey) return null;
  const item = state.workItemsById[turn.finalAnswerItemKey];
  return item?.type === 'agent_message' ? item : null;
}

export function selectPendingHumanInputs(
  state: WorkItemProjectionState,
  runId?: string,
  runVersion?: number,
  schemaVersion?: number
): HumanInputItem[] {
  const candidates = runId
    ? runIndexes(state, runId, runVersion).flatMap((index) =>
        Object.keys(index.pendingHumanInputKeys)
          .map((interactionId) => state.pendingHumanInputsById[interactionId])
          .filter((item): item is HumanInputItem => Boolean(item))
      )
    : Object.values(state.pendingHumanInputsById);
  return candidates
    .filter(
      (item) =>
        (runId === undefined || item.runId === runId) &&
        (runVersion === undefined || item.runVersion === runVersion) &&
        (schemaVersion === undefined || item.schemaVersion === schemaVersion)
    )
    .sort(
      (left, right) =>
        left.firstSequence - right.firstSequence ||
        left.key.localeCompare(right.key)
    );
}
